import { CartUpdateEvent } from '@theme/events';

// Serialize recommendation additions, including controls re-created by a cart morph.
let adding = false;
const uncertainVariants = new Set();
const confirmedVariants = new Set();
const controls = () => document.querySelectorAll('pharmacy-cart-recommendation');
const root = () => window.Shopify?.routes?.root || '/';

function sectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
}

function countFromSections(sections) {
  for (const html of Object.values(sections || {})) {
    if (typeof html !== 'string') continue;
    const text = new DOMParser().parseFromString(html, 'text/html')
      .querySelector('[ref="cartItemCount"]')?.textContent?.trim();
    if (text && /^\d+$/.test(text)) return Number(text);
  }
  return null;
}

async function cartSnapshot(ids) {
  const url = new URL(window.location.href);
  url.searchParams.set('sections', ids.join(','));
  const [cartResponse, sectionsResponse] = await Promise.all([
    fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }),
    fetch(url.toString(), { headers: { Accept: 'application/json' }, cache: 'no-store' }),
  ]);
  if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Cart refresh failed');
  const [cart, sections] = await Promise.all([cartResponse.json(), sectionsResponse.json()]);
  if (!Number.isInteger(cart.item_count) || !ids.every((id) => typeof sections[id] === 'string')) {
    throw new Error('Incomplete cart refresh');
  }
  return { itemCount: cart.item_count, sections };
}

function publishSnapshot(snapshot) {
  // This theme uses the same event for add/update and normally auto-opens on both.
  // Do not reopen a drawer the customer closed while the request was in flight.
  const autoOpenDrawers = [...document.querySelectorAll('cart-drawer-component[auto-open]')];
  autoOpenDrawers.forEach((drawer) => drawer.removeAttribute('auto-open'));
  try {
    document.dispatchEvent(new CartUpdateEvent({}, 'pharmacy-cart-recommendations', {
      source: 'pharmacy-cart-recommendations',
      itemCount: snapshot.itemCount,
      sections: snapshot.sections,
    }));
  } finally {
    autoOpenDrawers.forEach((drawer) => drawer.setAttribute('auto-open', ''));
  }
}

function announce(message, dialog) {
  if (!dialog?.isConnected || !dialog.open) return;
  let region = dialog.querySelector('[data-pharmacy-cart-announcement]');
  if (!region) {
    region = document.createElement('p');
    region.className = 'visually-hidden';
    region.dataset.pharmacyCartAnnouncement = '';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    dialog.append(region);
  }
  requestAnimationFrame(() => { region.textContent = message; });
}

class PharmacyCartRecommendation extends HTMLElement {
  #pending = false;
  #observer = new MutationObserver(() => this.syncControls());

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.syncControls();
    // Horizon morphs existing elements without reconnecting them; restore the
    // control state after server markup inserts its safe disabled default.
    this.#observer.observe(this, { childList: true, subtree: true, attributes: true,
      attributeFilter: ['disabled', 'value', 'selected'] });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  get choice() {
    const input = this.querySelector('[data-recommendation-variant]');
    const option = input?.tagName === 'SELECT' ? input.selectedOptions[0] : null;
    if (!input?.value || option?.disabled) return null;
    return { id: input.value, label: option?.textContent?.trim(), price: option?.dataset.price };
  }

  syncControls() {
    const button = this.querySelector('[data-recommendation-add]');
    const select = this.querySelector('select');
    if (!button) return;
    const choice = this.choice;
    const disabled = adding || !choice || uncertainVariants.has(choice.id) || confirmedVariants.has(choice.id);
    if (button.disabled !== disabled) button.disabled = disabled;
    button.setAttribute('aria-busy', String(this.#pending));
    button.setAttribute('aria-label', this.#pending ? this.dataset.addingLabel :
      `${this.dataset.addLabel}${choice?.label ? ` (${choice.label})` : ''}`);
    if (select && select.disabled !== adding) select.disabled = adding;
  }

  #showMessage(text, recovery = false) {
    const message = this.querySelector('.pharmacy-cart-recommendation-message');
    const link = this.querySelector('.pharmacy-cart-recommendation-recovery');
    if (message) { message.textContent = text; message.hidden = !text; }
    if (link) link.hidden = !recovery;
  }

  #onChange = () => {
    const choice = this.choice;
    const price = this.closest('.pharmacy-cart-recommendation')?.querySelector('[data-recommendation-price]');
    if (choice?.price && price) price.textContent = choice.price;
    this.#showMessage(uncertainVariants.has(choice?.id) ? this.dataset.uncertainError : '', uncertainVariants.has(choice?.id));
    this.syncControls();
  };

  #onClick = async (event) => {
    const button = event.target.closest?.('[data-recommendation-add]');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    if (adding || this.#pending) return;
    const choice = this.choice;
    if (!choice) { this.#showMessage(this.dataset.choiceError); return; }
    if (uncertainVariants.has(choice.id) || confirmedVariants.has(choice.id)) return;

    const ids = sectionIds();
    const dialog = this.closest('dialog');
    const originalFocus = document.activeElement;
    const hadFocus = this.contains(originalFocus);
    const messages = { ...this.dataset };
    const productId = this.dataset.productId;
    let accepted = false;
    let rejected = false;
    let errorMessage = '';
    adding = this.#pending = true;
    this.#showMessage('');
    controls().forEach((control) => control.syncControls());

    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: choice.id, quantity: 1 }], sections: ids,
          sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        rejected = true;
        errorMessage = typeof result.description === 'string' ? result.description : messages.addError;
        // An inventory-limit response may still change the cart. Reconcile without retrying the POST.
        try { publishSnapshot(await cartSnapshot(ids)); } catch { /* Preserve the original rejection. */ }
      } else {
        accepted = true;
        confirmedVariants.add(choice.id);
        let snapshot = { itemCount: countFromSections(result.sections), sections: result.sections };
        if (snapshot.itemCount === null || !ids.every((id) => typeof result.sections?.[id] === 'string')) {
          snapshot = await cartSnapshot(ids);
        }
        publishSnapshot(snapshot);
        confirmedVariants.delete(choice.id);
        announce(messages.addedMessage, dialog);
      }
    } catch {
      if (accepted) {
        errorMessage = messages.refreshError;
      } else {
        uncertainVariants.add(choice.id);
        errorMessage = messages.uncertainError;
        // A lost response is not proof the addition failed. Never repeat the mutation automatically.
        try { publishSnapshot(await cartSnapshot(ids)); } catch { /* Offer a normal cart link. */ }
      }
    } finally {
      adding = this.#pending = false;
      controls().forEach((control) => control.syncControls());
      if (errorMessage) {
        const current = [...controls()].find((control) => control.dataset.productId === productId);
        current?.#showMessage(errorMessage, !rejected);
        announce(errorMessage, dialog);
      }
      requestAnimationFrame(() => {
        if (hadFocus && !originalFocus?.isConnected && dialog?.open &&
          (document.activeElement === document.body || !document.activeElement?.isConnected)) {
          dialog.querySelector('.cart-drawer__close-button')?.focus({ preventScroll: true });
        }
      });
    }
  };
}

if (!customElements.get('pharmacy-cart-recommendation')) {
  customElements.define('pharmacy-cart-recommendation', PharmacyCartRecommendation);
}
